import { CHECKOUT_THEMES, type CheckoutTheme } from '@getjust/leaf';
import Head from 'next/head';
import { useEffect } from 'react';

import { useIsDesktop } from '$src/hooks/client/useIsDesktop';
import { useShop } from '$src/hooks/queries';
import { useROBrandingPreviewAtom } from '$src/hooks/state/useBrandingPreviewAtom';

import { isFeatureEnabled } from '../../hooks/state/useCheckoutFeaturesAtom';

let isMounted = false;

/**
 * This component is used to inject the background image into the body of the page.
 * Only fired with new url path (with shopid)
 */
export const ServerInjection = ({ shopId }: { shopId?: string }) => {
  const { data: shop } = useShop(shopId);
  const { isMobile } = useIsDesktop();
  const brandingPreview = useROBrandingPreviewAtom();

  // Reactively update background when branding preview changes (partner dashboard iframe)
  useEffect(() => {
    if (!brandingPreview) return;

    const previewBg = brandingPreview.backgroundUrl;

    if (previewBg) {
      const background = isMobile
        ? `url(${previewBg}) center center / cover no-repeat`
        : `linear-gradient(0deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1)), url(${previewBg}) no-repeat center/cover`;
      document.body.style.setProperty('--bg-brand', background);
      document.body.classList.add('show-background');
    } else {
      document.body.style.removeProperty('--bg-brand');
      document.body.classList.remove('show-background');
    }
  }, [brandingPreview?.backgroundUrl, isMobile]);

  if (!shop) {
    return null;
  }

  const logoUrl = shop.logo ? `${shop.baseMediaUrl}${shop.logo}` : undefined;
  const faviconUrl = shop.favicon ? `${shop.baseMediaUrl}${shop.favicon}` : undefined;
  const iconUrl = faviconUrl ?? '/favicon.ico';

  const backgroundUrl = shop.backgroundUrl
    ? shop.backgroundUrl.includes('https://')
      ? shop.backgroundUrl
      : `${shop.baseMediaUrl}${shop.backgroundUrl}`
    : undefined;

  const shopTheme = shop.settings?.checkoutTheme;
  const themeName: CheckoutTheme =
    shopTheme && CHECKOUT_THEMES.includes(shopTheme) ? (shopTheme as CheckoutTheme) : 'aurora';
  const isTransparentCustom =
    themeName === 'custom' && shop.settings?.brandStyles?.background === 'transparent';

  const isClientSide = typeof window !== 'undefined' && typeof document !== 'undefined';
  const shouldInjectBackground = !isMounted && isClientSide;
  const missingShopBackground = !backgroundUrl;

  if (shouldInjectBackground) {
    isMounted = true;

    document.body.classList.add('redirect');

    if (isTransparentCustom) {
      document.body.setAttribute('data-transparent', 'true');
    }

    // add a basic grey linear gradient (brand hasn't set any background image)
    if (missingShopBackground) {
      const background = `repeat linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.8)), url(${logoUrl})`;
      document.body.style.setProperty('--bg-brand', background);
      document.body.classList.add('show-background');
    }

    const imgSet = [`url(${backgroundUrl}) 1x`];

    if (shop.bgVariants?.length) {
      imgSet.push(
        ...shop.bgVariants.map((variant) => `url(${shop.baseMediaUrl}${variant.fileKey}) ${variant.name}`),
      );
    }

    // desktop background image
    if (!isMobile && !missingShopBackground) {
      const background = `
          linear-gradient(0deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1)),
          image-set(${imgSet.join(', ')}) no-repeat center/cover
        `;
      document.body.style.setProperty('--bg-brand', background);
      document.body.classList.add('show-background');
    }

    // only show mobile background if the feature is enabled
    if (isMobile && isFeatureEnabled(shop, 'show_mobile_background_image')) {
      const background = `image-set(${imgSet.join(', ')}) center center / cover no-repeat`;
      document.body.style.setProperty('--bg-brand', background);
      document.body.classList.add('show-background');
    }
  }

  return (
    <Head>
      <title>{shop.name}</title>
      {iconUrl && <link rel="icon" href={iconUrl} />}
      {logoUrl && <link rel="preload" href={logoUrl} as="image" />}
      {!missingShopBackground && <link rel="preload" href={backgroundUrl} as="image" />}
      {shop.bgVariants?.map((variant) => (
        <link
          rel="preload"
          href={`${shop.baseMediaUrl}${variant.fileKey}`}
          as="image"
          key={variant.fileKey}
        />
      ))}
    </Head>
  );
};
